import { inject } from '@angular/core';
import { map } from 'rxjs';
import { signalStore } from '@ngrx/signals';
import { withEntities } from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { BillingQuoteLineService } from './generated/billing-quote-line/billing-quote-line.service';
import { QuotesStore } from './quotes.store';
import { withChargeLines } from './charge-lines.feature';
import { WorkChargeKindEnum } from './generated/types/workChargeKindEnum';
import type {
  PatchedQuoteLineUpdateRequestV1Request,
  QuoteLineCreateRequestV1Request,
  QuoteLineV1,
} from './generated/types';

/** The endpoint maximum: one page holds a whole section. */
const PAGE_SIZE = 1000;

/**
 * Lines of ONE section of ONE quote. Same shape as `WorkChargeLinesStore`: the cursor
 * `Query` is the PARENT identity (`load(quoteIdentity)`) and the mutations come from
 * `withChargeLines`. Both sections live on one endpoint (M-bill-36): the listing keeps
 * its section with `?section=`, and every line a caller creates names its section. A
 * PATCH with the other `section` moves the line; the caller reloads the other store.
 *
 * Every mutation answers the whole quote with its totals recomputed: `frm-quote` takes
 * it from the returned Observable (a new quote is not among the `QuotesStore` entities)
 * and `syncQuote` keeps `dt-quotes` behind the modal in step.
 *
 * ponytail: one page of 1000 per section and no `loadMore` caller; paginate if a quote
 * ever gets near that.
 */
function quoteLinesStore(section: WorkChargeKindEnum) {
  return signalStore(
    withEntities<QuoteLineV1>(),
    withCursorPagination<QuoteLineV1, string>(() => {
      const api = inject(BillingQuoteLineService);
      return (identity, cursor) =>
        api.billingV1QuoteLinesList(
          { identity },
          { cursor: cursor ?? undefined, page_size: PAGE_SIZE, section },
        );
    }),
    withChargeLines(() => {
      const api = inject(BillingQuoteLineService);
      const quotes = inject(QuotesStore);
      return {
        create: (identity, body: QuoteLineCreateRequestV1Request) =>
          api
            .billingV1QuoteLinesCreate({ identity }, body)
            .pipe(map((res) => res.quote)),
        update: (identity, lineIdentity, body: PatchedQuoteLineUpdateRequestV1Request) =>
          api
            .billingV1QuoteLinesPartialUpdate({ identity, lineIdentity }, body)
            .pipe(map((res) => res.quote)),
        destroy: (identity, lineIdentity) =>
          api.billingV1QuoteLinesDestroy({ identity, lineIdentity }),
        syncParent: (quote) => {
          quotes.syncQuote(quote);
        },
        refreshParent: (identity) => quotes.refreshQuote(identity),
      };
    }),
  );
}

/** Section 2 of the quote: installation lines. */
export const QuoteInstallationLinesStore = quoteLinesStore(WorkChargeKindEnum.installation);

/** Section 4 of the quote: service lines. */
export const QuoteServiceLinesStore = quoteLinesStore(WorkChargeKindEnum.service);
